import { startTransition, useEffect } from 'react'

import { Helmet } from 'react-helmet-async'

import { useCommandBarSetContext } from '@tribeplatform/react-components/CommandBar'
import { useDomain } from '@tribeplatform/react-components/common/hooks'
import { useRouterPath } from '@tribeplatform/react-sdk'
import { useReadSpace } from '@tribeplatform/react-sdk/hooks'
import {
  SpaceType,
  type Network,
  type Space as TSpace,
} from '@tribeplatform/react-sdk/types'
import {
  SlateRenderer,
  SlateRendererProps,
} from '@tribeplatform/react-slate-kit/components'
import { Container } from '@tribeplatform/react-ui-kit/Layout'

import { useAnalytics } from '../../hooks/useAnalytics.js'
import { PageLayout } from '../Layout/PageLayout.js'
import { NewSpaceBanner } from './NewSpaceBanner.js'
import { GenericSpaceLoading } from './SpaceLoading.js'
import { getSlateLayoutFromString } from './utils.js'

interface Props {
  network: Network
  space?: TSpace
  context: SlateRendererProps['context']
}
export const Space = ({ network, space, context }: Props) => {
  const { mutateAsync: readSpace } = useReadSpace()
  const { getResourceUrl } = useDomain()
  const { setCommandBarContext, unsetCommandBarContext } =
    useCommandBarSetContext()
  const pathname = useRouterPath()
  const analytics = useAnalytics({
    target: { spaceId: space?.id, spaceType: space?.type },
  })

  const { title: pageTitle = space?.name || space?.slug } =
    space?.seoDetail || {}
  const title = `${pageTitle} - ${network?.name}`
  const slateContext: SlateRendererProps['context'] = {
    ...context,
    space,
    spaceId: space?.id,
  }
  const layout = getSlateLayoutFromString(space?.layout)

  useEffect(() => {
    if (!space?.id) {
      return undefined
    }

    analytics.trackPageView(title)
    if (
      space?.type === SpaceType.Group &&
      space?.authMemberProps?.unreadPostsCount > 0
    ) {
      readSpace({ spaceId: space?.id, date: new Date().toISOString() })
    }

    if (space?.type === SpaceType.Group || space?.type === SpaceType.Page) {
      startTransition(() => {
        setCommandBarContext({ entity: space, entityType: 'space' })
      })
    }

    return () => unsetCommandBarContext({ entity: space, entityType: 'space' })
  }, [
    title,
    space,
    analytics,
    readSpace,
    setCommandBarContext,
    unsetCommandBarContext,
  ])

  if (!space) {
    return (
      <PageLayout slateContext={slateContext}>
        <Container
          size="full"
          padding={{
            vertical: 'md',
            horizontal: { size: 'md', disableOnMobile: true },
          }}
        >
          <GenericSpaceLoading />
        </Container>
      </PageLayout>
    )
  }

  return (
    <PageLayout layout={layout} slateContext={slateContext}>
      <Helmet>
        {space.type === SpaceType.Group && pathname !== '/' && (
          <link
            rel="alternate"
            type="application/rss+xml"
            title={space.name}
            href={getResourceUrl(`/rss/${space.id}/posts`)}
          />
        )}
      </Helmet>
      <NewSpaceBanner space={space} />
      <SlateRenderer slate={space.slate} context={slateContext} />
    </PageLayout>
  )
}
